import { nairaFromMinor } from '../../lib/format'
import { displayTitle } from '../../lib/displayName'
import { useMemo } from 'react'
import { compareTimestamps } from '@ovasabi/frontend-kit'
import {
  useHelpTicketSnapshot,
  useMarketplaceOrderSnapshot,
  useChowProfileSnapshot,
  useVendorProfileSnapshot,
} from '../../generated/prototypeRuntime'

/**
 * Reads for the operator console.
 *
 * Everything here comes from the same tenant projections the apps read — there
 * is no separate operator query path, and deliberately so: an operator seeing
 * different data from the person they are helping is how support conversations
 * go wrong. What an operator has that a customer does not is capability (the
 * `ops` role's `marketplace.*`, `billing.request_refund`, `profile.*`), which
 * the backend checks on the write commands, not a wider read.
 */

export type Row = Record<string, unknown>

const text = (value: unknown, fallback = ''): string => {
  const raw = String(value ?? '').trim()
  return raw === '' ? fallback : raw
}

/** Strip prototype/dummy rows and seeded test rows so the console never reports fictional numbers. */
const isReal = (row: Row): boolean => {
  const id = String(row.id ?? '')
  if (id === '' || id.includes('-prototype') || /^(order|thread|message|transaction|ticket)-/.test(id)) {
    return false
  }
  const meta = row.metadata as Record<string, unknown> | undefined
  if (meta && (meta.seed === true || meta.seed === 'true')) {
    return false
  }
  if (meta && (meta.review_seed === true || meta.review_seed === 'true')) {
    return false
  }
  if (meta && (meta.deleted === true || meta.deleted === 'true')) {
    return false
  }
  return true
}

export interface VendorRow {
  id: string
  profileId: string
  displayName: string
  vendorType: string
  cuisine: string
  area: string
  verification: 'unverified' | 'pending' | 'verified' | 'suspended' | 'rejected' | 'revoked'
  badge: string
  isOpen: boolean
  createdAt: string
}

const shortEnum = (value: unknown, fallback = ''): string => {
  const raw = text(value, fallback)
  if (!/^[A-Z0-9_]+$/.test(raw)) return raw.toLowerCase()
  return raw.replace(/^(?:[A-Z0-9]+_)*?(?:STATUS|TYPE|STATE)_/, '').toLowerCase()
}

export const useOpsVendors = (): readonly VendorRow[] => {
  const snapshot = useVendorProfileSnapshot() as { records: readonly Row[] }
  return useMemo(
    () =>
      snapshot.records.filter(isReal).map((v) => {
        const verification = shortEnum(v.verification_status, 'unverified')
        return {
          id: text(v.id),
          profileId: text(v.profile_id),
          displayName: displayTitle(v.display_name) || 'Unnamed kitchen',
          vendorType: shortEnum(v.vendor_type, 'home_chef'),
          cuisine: text(v.cuisine),
          area: text(v.location_label),
          verification: (['unverified', 'pending', 'verified', 'suspended', 'rejected', 'revoked'].includes(verification)
            ? verification
            : 'unverified') as VendorRow['verification'],
          badge: text(v.badge_tier, 'none'),
          isOpen: Boolean(v.is_open),
          createdAt: text(v.created_at),
        }
      }),
    [snapshot.records],
  )
}

export interface OrderRow {
  id: string
  reference: string
  status: string
  customerProfileId: string
  merchantProfileId: string
  totalMinor: number
  deliveryChoice: string
  createdAt: string
  updatedAt: string
}

export const useOpsOrders = (): readonly OrderRow[] => {
  const snapshot = useMarketplaceOrderSnapshot() as { records: readonly Row[] }
  return useMemo(
    () =>
      snapshot.records
        .filter(isReal)
        .map((o) => ({
          id: text(o.id),
          reference: text(o.reference, text(o.id).slice(0, 8)),
          status: shortEnum(o.status, 'pending_payment'),
          customerProfileId: text(o.customer_profile_id),
          merchantProfileId: text(o.merchant_profile_id),
          totalMinor: Number(o.total_minor ?? 0),
          deliveryChoice: shortEnum(o.delivery_choice, 'standard'),
          createdAt: text(o.created_at),
          updatedAt: text(o.updated_at),
        }))
        .sort((a, b) => compareTimestamps(a.createdAt, b.createdAt, a.id, b.id, { direction: 'desc' })),
    [snapshot.records],
  )
}

export interface TicketRow {
  id: string
  subject: string
  body: string
  status: string
  channel: string
  slaLevel: string
  profileId: string
  orderId: string
  createdAt: string
}

export const useOpsTickets = (): readonly TicketRow[] => {
  const snapshot = useHelpTicketSnapshot() as { records: readonly Row[] }
  return useMemo(
    () =>
      snapshot.records
        .filter(isReal)
        .map((t) => ({
          id: text(t.id),
          subject: text(t.subject, 'Support request'),
          body: text(t.body),
          status: shortEnum(t.status, 'open'),
          channel: shortEnum(t.channel, 'in_app'),
          slaLevel: shortEnum(t.sla_level, 'standard'),
          profileId: text(t.profile_id),
          orderId: text(t.order_id),
          createdAt: text(t.created_at),
        }))
        .sort((a, b) => compareTimestamps(a.createdAt, b.createdAt, a.id, b.id, { direction: 'desc' })),
    [snapshot.records],
  )
}

/** profile_id → display name, for putting names on ids in the console. */
export const useProfileNames = (): ReadonlyMap<string, string> => {
  const snapshot = useChowProfileSnapshot() as { records: readonly Row[] }
  return useMemo(
    () => new Map(snapshot.records.map((p) => [text(p.id), displayTitle(p.display_name) || 'Unknown'])),
    [snapshot.records],
  )
}

/** Naira from minor units, with no fractional kobo noise. */
export const formatNaira = nairaFromMinor

export const formatWhen = (iso: string): string => {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-NG', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}
